import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { HttpClientService } from "./http-client.service";
import { Observable } from "rxjs";
import { map, shareReplay, tap } from "rxjs/operators";
import { EnvironmentService } from "./environment.service";
import {
  AssignPortfolioResponse,
  BucketConfigurationResponse,
  CancelMoveResponse,
  CancelRemovalResponse,
  CollectorAccountExport,
  CollectorAccountExportCreateResponse,
  CollectorOption,
  CollectorPerformanceSummary,
  CreateAssignmentPayload,
  DeleteAssignmentResponse,
  MoveCollectorPayload,
  MoveCollectorResponse,
  PortfolioAssignmentRun,
} from "../types/preventive-collection.types";

@Injectable()
export class PreventiveCollectionService {
  private adminCollectors$: Observable<CollectorOption[]> | null = null;

  constructor(
    private http: HttpClientService,
    private raw_http: HttpClient,
    private environmentService: EnvironmentService
  ) {}

  private buildBucketPhaseParams(bucket_id?: number | string, phase_id?: number | string): Record<string, any> {
    const params: Record<string, any> = {};

    if (bucket_id !== undefined && bucket_id !== "") {
      params.bucket_id = bucket_id;
    }

    if (phase_id !== undefined && phase_id !== "") {
      params.phase_id = phase_id;
    }

    return params;
  }

  public getCollectorAccounts(
    page = 1,
    per_page = 20,
    filters?: any,
    bucket_id?: number | string,
    phase_id?: number | string,
    sort_by?: string,
    sort_direction?: "asc" | "desc",
    all_clients?: boolean,
    collector_id?: number
  ): Observable<any> {
    const params: any = {
      page,
      per_page,
      ...filters,
      ...this.buildBucketPhaseParams(bucket_id, phase_id),
    };
    if (sort_by) {
      params.sort_by = sort_by;
    }
    if (sort_direction) {
      params.sort_direction = sort_direction;
    }
    if (all_clients !== undefined) {
      params.all_clients = String(all_clients);
    }
    if (collector_id != null) {
      params.collector_id = collector_id;
    }
    if (params.late_payments_last_6_months != null) {
      params.late_payments_last_6_months = params.late_payments_last_6_months.join(",");
    }
    if (params.state != null) {
      params.state = params.state.join(",");
    }
    if (params.payment_number != null) {
      params.payment_number = params.payment_number.join(",");
    }

    const options = {
      params,
    };

    return this.http
      .get("/api/preventive_collection/collector_accounts", options)
      .pipe(tap((_) => console.log("PreventiveCollectionService.getCollectorAccounts: Ok.")));
  }

  public createCollectorAccountExport(
    filters?: any,
    bucket_id?: number | string,
    phase_id?: number | string,
    exportOptions?: {
      export_type?: string;
      year?: number;
      month?: number;
      cutoff?: number;
    },
    all_clients?: boolean,
    collector_id?: number
  ): Observable<CollectorAccountExportCreateResponse> {
    const payload: any = {
      ...filters,
      ...this.buildBucketPhaseParams(bucket_id, phase_id),
      ...exportOptions,
    };
    if (all_clients !== undefined) {
      payload.all_clients = String(all_clients);
    }
    if (collector_id != null) {
      payload.collector_id = collector_id;
    }

    return this.http
      .post("/api/preventive_collection/collector_account_exports", payload)
      .pipe(tap((_) => console.log("PreventiveCollectionService.createCollectorAccountExport: Ok.")));
  }

  public getCollectorAccountExport(id: number | string): Observable<CollectorAccountExport> {
    return this.http
      .get(`/api/preventive_collection/collector_account_exports/${id}`)
      .pipe(tap((_) => console.log("PreventiveCollectionService.getCollectorAccountExport: Ok.")));
  }

  public downloadCollectorAccountExport(id: number | string): Observable<Blob> {
    const access_token = localStorage.getItem("access_token");
    const endpoint = this.environmentService.get("api.url") || "";
    const url = `${endpoint}/api/preventive_collection/collector_account_exports/${id}/download`;

    const headers = new HttpHeaders({
      Authorization: `Bearer ${access_token}`,
      "ngsw-bypass": "true",
    });

    return this.raw_http
      .get(url, { responseType: "blob", headers })
      .pipe(tap((_) => console.log("PreventiveCollectionService.downloadCollectorAccountExport: Ok.")));
  }

  public getAccountDetails(id: string | number): Observable<any> {
    return this.http
      .get(`/api/preventive_collection/collector_accounts/${id}`)
      .pipe(tap((_) => console.log("PreventiveCollectionService.getAccountDetails: Ok.")));
  }

  public createDation(accountId: number): Observable<unknown> {
    return this.http
      .post("/api/closures/create_dation", { account_id: accountId })
      .pipe(tap((_) => console.log("PreventiveCollectionService.createDation: Ok.")));
  }

  public registerContact(id: string | number, contact_data: any): Observable<any> {
    return this.http
      .post(`/api/preventive_collection/collector_accounts/${id}/register_contact`, contact_data)
      .pipe(tap((_) => console.log("PreventiveCollectionService.registerContact: Ok.")));
  }

  public getDashboard(
    bucket_id?: number | string,
    phase_id?: number | string,
    all_clients?: boolean,
    collector_id?: number
  ): Observable<any> {
    const params: any = this.buildBucketPhaseParams(bucket_id, phase_id);
    if (all_clients !== undefined) {
      params.all_clients = String(all_clients);
    }
    if (collector_id != null) {
      params.collector_id = collector_id;
    }
    const options: any = { params };

    return this.http
      .get("/api/preventive_collection/collector_accounts/dashboard", options)
      .pipe(tap((_) => console.log("PreventiveCollectionService.getDashboard: Ok.")));
  }

  public getCollectorPerformance(collector_id?: number): Observable<CollectorPerformanceSummary> {
    const params: { [param: string]: string } = {};
    if (collector_id != null) {
      params.collector_id = String(collector_id);
    }
    const options = Object.keys(params).length ? { params } : {};

    return this.http
      .get<CollectorPerformanceSummary>("/api/preventive_collection/collector_accounts/performance", options)
      .pipe(tap((_) => console.log("PreventiveCollectionService.getCollectorPerformance: Ok.")));
  }

  public getCollectorData(bucket_id?: number | string, phase_id?: number | string): Observable<any> {
    const options: any = {
      params: this.buildBucketPhaseParams(bucket_id, phase_id),
    };

    return this.http
      .get("/api/preventive_collection/collector_accounts/collector_data", options)
      .pipe(tap((_) => console.log("PreventiveCollectionService.getCollectorData: Ok.")));
  }

  public getCollectionHistories(
    account_id: number,
    filters?: {
      management_type?: string;
      management_types?: string[];
      category?: string;
      exclude_notes?: boolean;
      user_id?: number | string | null;
      start_date?: string;
      end_date?: string;
    }
  ): Observable<any> {
    const params: any = {};
    if (filters) {
      if (filters.management_type) {
        params.management_type = filters.management_type;
      }
      if (filters.management_types && filters.management_types.length > 0) {
        params["management_type[]"] = filters.management_types;
      }
      if (filters.category) {
        params.category = filters.category;
      }
      if (filters.exclude_notes) {
        params.exclude_notes = "true";
      }
      if (filters.user_id !== undefined) {
        params.user_id = filters.user_id;
      }
      if (filters.start_date) {
        params.start_date = filters.start_date;
      }
      if (filters.end_date) {
        params.end_date = filters.end_date;
      }
    }

    const options = {
      params,
    };

    return this.http
      .get(`/api/accounts/${account_id}/collection_histories/search`, options)
      .pipe(tap((_) => console.log("PreventiveCollectionService.getCollectionHistories: Ok.")));
  }

  public createCollectionHistory(
    account_id: number,
    data: {
      management_type: string;
      note: string;
      collector_account_id: number | string;
    }
  ): Observable<any> {
    const payload = { collection_history: data };
    return this.http
      .post(`/api/accounts/${account_id}/collection_histories`, payload)
      .pipe(tap((_) => console.log("PreventiveCollectionService.createCollectionHistory: Ok.")));
  }

  public getBucketConfiguration(): Observable<BucketConfigurationResponse> {
    return this.http
      .get("/api/preventive_collection/admin/bucket_configuration")
      .pipe(tap((_) => console.log("PreventiveCollectionService.getBucketConfiguration: Ok.")));
  }

  public getAdminCollectorsCompact(): Observable<CollectorOption[]> {
    if (!this.adminCollectors$) {
      this.adminCollectors$ = this.http
        .get("/api/preventive_collection/admin/collectors", { params: { compact: "true" } })
        .pipe(
          map((res: { collectors?: CollectorOption[] } | null) => res?.collectors ?? []),
          tap({
            next: () => console.log("PreventiveCollectionService.getAdminCollectorsCompact: Ok."),
            // shareReplay(1) would otherwise cache the errored observable
            // forever; invalidate it so the next subscription retries.
            error: () => (this.adminCollectors$ = null),
          }),
          shareReplay(1)
        );
    }
    return this.adminCollectors$;
  }

  public clearAdminCollectorsCache(): void {
    this.adminCollectors$ = null;
  }

  public moveCollector(payload: MoveCollectorPayload): Observable<MoveCollectorResponse> {
    return this.http
      .put("/api/preventive_collection/admin/move_collector", payload)
      .pipe(tap((_) => console.log("PreventiveCollectionService.moveCollector: Ok.")));
  }

  public deleteAssignment(assignmentId: number): Observable<DeleteAssignmentResponse> {
    return this.http
      .delete(`/api/preventive_collection/admin/assignments/${assignmentId}`)
      .pipe(tap((_) => console.log("PreventiveCollectionService.deleteAssignment: Ok.")));
  }

  public createPortfolioAssignmentRun(): Observable<PortfolioAssignmentRun> {
    return this.http
      .post("/api/preventive_collection/admin/portfolio_assignment_runs", {})
      .pipe(tap((_) => console.log("PreventiveCollectionService.createPortfolioAssignmentRun: Ok.")));
  }

  public getPortfolioAssignmentRun(runId: number): Observable<PortfolioAssignmentRun> {
    return this.http
      .get(`/api/preventive_collection/admin/portfolio_assignment_runs/${runId}`)
      .pipe(tap((_) => console.log("PreventiveCollectionService.getPortfolioAssignmentRun: Ok.")));
  }

  /** @deprecated Use createPortfolioAssignmentRun and poll getPortfolioAssignmentRun instead. */
  public assignPortfolio(): Observable<AssignPortfolioResponse> {
    return this.http
      .post("/api/preventive_collection/admin/assign_portfolio", {})
      .pipe(tap((_) => console.log("PreventiveCollectionService.assignPortfolio: Ok.")));
  }

  public createAssignment(payload: CreateAssignmentPayload): Observable<{ message: string }> {
    return this.http
      .post("/api/preventive_collection/admin/assignments", payload)
      .pipe(tap((_) => console.log("PreventiveCollectionService.createAssignment: Ok.")));
  }

  public cancelMove(assignmentId: number): Observable<CancelMoveResponse> {
    return this.http
      .put("/api/preventive_collection/admin/cancel_move", { assignment_id: assignmentId })
      .pipe(tap((_) => console.log("PreventiveCollectionService.cancelMove: Ok.")));
  }

  public cancelRemoval(assignmentId: number): Observable<CancelRemovalResponse> {
    return this.http
      .put("/api/preventive_collection/admin/cancel_removal", { assignment_id: assignmentId })
      .pipe(tap((_) => console.log("PreventiveCollectionService.cancelRemoval: Ok.")));
  }
}
